export const pageData = {
  careers: {
    title: "Careers at Billion Towers",
    eyebrow: "Join our team",
    subtitle:
      "Building institutional-grade infrastructure for tokenized real estate.",
    description:
      "We're recruiting experienced builders across engineering, product, compliance, and operations who are passionate about bringing traditional real estate economics onchain. Billion Towers combines fintech expertise with deep real estate knowledge to create a transparent, institutional-grade platform.",
    sections: [
      {
        heading: "Why Billion Towers?",
        text: "Our mission is to democratize access to premium real estate investments while maintaining institutional-grade standards for risk management and transparency. We partner with leading institutions to tokenize verified real estate assets and provide investors with clear ownership, reporting, and liquidity pathways. This is infrastructure work with real-world impact.",
        items: [
          "Work on real assets backed by institutional partnerships and regulatory oversight",
          "Culture of transparency—every decision is grounded in investor protection",
          "Opportunity to shape product direction and market strategy",
          "Competitive compensation with meaningful equity ownership",
        ],
      },
      {
        heading: "Open roles",
        items: [
          "Senior Solidity Engineer — Design and audit smart contracts for asset tokenization, custody, and settlement. Build secure, audited code that underpins real estate transactions.",
          "Full-Stack Engineer — Own the investor dashboard, marketplace UX, and API infrastructure. Integrate with blockchain, custody providers, and data services.",
          "Senior Compliance Officer — Lead AML/KYC frameworks, investor accreditation programs, and regulatory strategy. Work directly with legal counsel and regulators.",
          "Real Estate Operations Manager — Source and evaluate properties, manage asset partnerships, perform operational due diligence on sponsors.",
          "Product Manager, Institutional — Own partnerships with real estate sponsors and institutional investors. Define product requirements for scale.",
          "Security Engineer — Implement custody controls, audit blockchain integrations, and manage platform security posture.",
          "Data Engineer — Build data pipelines for property performance metrics, investor reporting, and market analytics.",
        ],
      },
      {
        heading: "Requirements",
        items: [
          "Demonstrated experience in fintech, banking, or regulated markets (minimum 4+ years)",
          "Deep understanding of blockchain architecture and smart contract best practices",
          "Track record shipping products that handle compliance, security, and user trust",
          "Strong communication skills—ability to translate complex topics for non-technical stakeholders",
          "Alignment with institutional-grade execution and bias toward getting things right",
        ],
      },
      {
        heading: "Our team",
        text: "We're backed by experienced institutional investors and led by founders with backgrounds at leading fintech companies, real estate firms, and blockchain infrastructure teams. Our advisors include compliance experts, real estate executives, and regulatory veterans.",
      },
    ],
    callout: {
      title: "Interested?",
      text: "Send your resume, GitHub profile, and a brief note on why you're interested in tokenized real estate to careers@billiontowers.com. Please include an example of a product or system you're proud of.",
      buttonText: "Apply via Email",
      buttonLink: "mailto:careers@billiontowers.com",
    },
  },
  press: {
    title: "Press & Media",
    eyebrow: "Newsroom",
    subtitle: "Latest news, press releases, and media coverage.",
    description:
      "Billion Towers is advancing institutional-grade infrastructure for tokenized real estate. Access our latest announcements, media kit, and executive resources.",
    sections: [
      {
        heading: "Latest announcements",
        items: [
          "Billion Towers Closes $50M Series A to Scale Tokenized Real Estate Platform — Led by Blockchain Capital and XYZ Ventures",
          "Partnership: Billion Towers and Major Real Estate Sponsor Launch First Tokenized Commercial Portfolio on Ethereum",
          "Regulatory Milestone: SEC Approves Billion Towers as ATS for Tokenized Asset Trading",
          "Goldman Sachs Joins as Custody Partner for Institutional Real Estate Tokens",
          "Billion Towers Platform Surpasses $1B in Assets Under Management",
        ],
      },
      {
        heading: "Media coverage",
        text: "Recent coverage from leading fintech and real estate media outlets.",
        items: [
          "Forbes: How Billion Towers is Bringing Real Estate Tokenization to Wall Street (May 2026)",
          "The Block: Billion Towers Raises $50M to Compete in Growing RWA Market (April 2026)",
          "CoinDesk: Tokenized Real Estate Assets Hit Inflection Point (March 2026)",
          "Financial Times: Blockchain Technology Reshapes Commercial Real Estate (February 2026)",
        ],
      },
      {
        heading: "Media Kit",
        text: "Brand assets, executive backgrounds, and company overview materials.",
        items: [
          "Brand Kit — Logos, colors, and style guidelines",
          "Executive Bios — Founder and leadership team backgrounds",
          "Product Overview — Market presentation and company deck",
          "Fact Sheet — Key metrics, funding history, and partnerships",
        ],
      },
      {
        heading: "Press inquiries",
        text: "For media questions, interviews, and story ideas:",
        items: [
          "press@billiontowers.com",
          "+252906262489",
          "Media relations team available during US business hours",
        ],
      },
    ],
  },
  blog: {
    title: "Insights & Research",
    eyebrow: "Knowledge hub",
    subtitle:
      "Analysis on tokenization, real estate markets, and digital asset infrastructure.",
    description:
      "Billion Towers publishes original research, market commentary, and educational content to help investors and institutions understand tokenized real estate.",
    sections: [
      {
        heading: "Featured research",
        items: [
          "The Case for Tokenized Real Estate: Why Institutions Are Moving Capital Onchain (Original Research)",
          "Custody Standards for Digital Assets: A Framework for Institutional Trust (Whitepaper)",
          "Market Structure: How Tokenization Changes Commercial Real Estate Economics (Analysis)",
          "Regulatory Roadmap: SEC, State, and International Frameworks for RWA (Compliance Deep Dive)",
        ],
      },
      {
        heading: "Recent articles",
        text: "Actionable insights and market updates for investors and institutions.",
        items: [
          "Liquidity Solutions for Tokenized Assets: Secondary Markets and AMMs (May 2026)",
          "Q1 2026 Real Estate Market Report: Property Values and Yield Trends (April 2026)",
          "Smart Contract Audits and Security: What to Look for in RWA Platforms (March 2026)",
          "Tax Implications of Tokenized Real Estate Ownership (February 2026)",
          "How to Evaluate a Tokenization Platform: Technical and Operational Due Diligence (January 2026)",
        ],
      },
      {
        heading: "Why subscribe?",
        text: "Stay informed on tokenized real estate developments, regulatory changes, and platform updates.",
        items: [
          "Weekly market digests and regulatory news",
          "In-depth research on asset opportunities and risk factors",
          "Exclusive interviews with real estate and blockchain experts",
          "Advanced analytics on platform performance and investor behavior",
        ],
      },
    ],
  },
  tokenomics: {
    title: "Tokenomics & Economics",
    eyebrow: "Platform sustainability",
    subtitle: "Economic design aligned with long-term institutional adoption.",
    description:
      "Billion Towers' economic model is designed to incentivize participation, ensure platform stability, and align stakeholder interests in bringing real estate assets onchain.",
    sections: [
      {
        heading: "Token supply & release",
        text: "A fixed supply of BTT tokens with a disciplined release schedule to support liquidity and governance.",
        items: [
          "Total supply: 1 billion BTT tokens (fixed cap)",
          "Initial allocation: 20% to early investors, 40% to treasury, 20% to team (4-year vest), 20% to ecosystem",
          "Monthly release of 5% of treasury allocation for liquidity, incentives, and operations",
          "Quarterly burn of platform transaction fees to reduce supply and support BTT holders",
        ],
      },
      {
        heading: "Fee structure",
        text: "Transparent, competitive fees designed to sustain operations while protecting investor economics.",
        items: [
          "Asset origination fees: 1-2% (paid by real estate sponsors at token issuance)",
          "Secondary market trading fees: 0.5% (split: 0.2% platform, 0.3% liquidity provider rewards)",
          "Custody and settlement fees: 0.15% annually (paid by asset sponsor)",
          "Investor operations fee: 0.25% annually (platform operations and support)",
        ],
      },
      {
        heading: "Incentives & rewards",
        text: "BTT holders earn returns through token appreciation, trading rebates, and governance participation.",
        items: [
          "Liquidity mining: Earn BTT for providing secondary market liquidity",
          "Trading rebates: BTT holders receive 10% discount on transaction fees",
          "Governance voting: Participate in platform decisions, fee structure updates, and partnerships",
          "Staking rewards: Optional lockups earn 8-12% APY depending on commitment period",
        ],
      },
      {
        heading: "Economic transparency",
        text: "Detailed reporting on token economics, platform revenue, and fee usage.",
        items: [
          "Monthly treasury reports published onchain",
          "Quarterly economic updates with usage metrics and projections",
          "Fee breakdown and allocation per asset class",
          "Smart contract audit results and custody provider reports",
        ],
      },
    ],
  },
  "mercury-pro": {
    title: "Mercury Pro",
    eyebrow: "Professional execution tools",
    subtitle: "Advanced trading infrastructure for institutional digital asset desks.",
    description:
      "Mercury Pro delivers smart order routing, market data aggregation, and real-time risk controls across venues for sophisticated electronic trading teams.",
    sections: [
      {
        heading: "Advanced execution tools",
        text: "A complete execution management system built for digital asset flow across multiple venues.",
        items: [
          "Multi-exchange connectivity for direct venue access",
          "Smart order routing to optimize execution across liquidity sources",
          "Real-time market data aggregation with actionable views",
          "Portfolio and position management integrated in one desk workflow",
        ],
      },
      {
        heading: "Control and oversight",
        items: [
          "Pre-trade risk checks and trade approval workflows",
          "Latency-sensitive order handling with deterministic performance",
          "Clear audit trails for every execution event",
          "Configurable access controls for trading teams and compliance",
        ],
      },
    ],
    callout: {
      title: "Schedule a demo",
      text: "See how Mercury Pro can accelerate execution and risk management for your desk.",
      buttonText: "Request a demo",
      buttonLink: "/contact",
    },
  },
  "mercury-otc": {
    title: "Mercury OTC",
    eyebrow: "Electronic OTC workflow",
    subtitle: "Replace voice and chat-based OTC trading with a fully electronic workflow.",
    description:
      "Mercury OTC supports RFQ/RFS flows, multi-dealer pricing, and embedded compliance for professional OTC trading teams.",
    sections: [
      {
        heading: "Automated OTC pricing",
        text: "Deliver continuous, electronic RFQ workflow with configurable pricing models.",
        items: [
          "24/7 automated RFQ/RFS quoting",
          "Multi-dealer pricing and liquidity aggregation",
          "Integrated compliance chat and documentation",
          "White-label deployment for broker dealers and market makers",
        ],
      },
      {
        heading: "Operational efficiency",
        items: [
          "Structured quote management and execution tracking",
          "Centralized trade capture for OTC desks",
          "Compliance workflows embedded into every RFQ",
          "Transparent margin and pricing analysis",
        ],
      },
    ],
    callout: {
      title: "Discover OTC execution",
      text: "Modernize your bilateral trading workflow with stronger controls and automation.",
      buttonText: "Contact sales",
      buttonLink: "/contact",
    },
  },
  "mercury-rwa": {
    title: "Mercury RWA",
    eyebrow: "Tokenized asset markets",
    subtitle: "Secondary markets for tokenized assets with compliant market access.",
    description:
      "Mercury RWA provides trading infrastructure for tokenized real-world assets, including secondary price discovery and settlement workflows.",
    sections: [
      {
        heading: "Institutional asset distribution",
        text: "Support compliant issuance, custody, and trading for tokenized real-world assets.",
        items: [
          "Tokenized asset marketplace with continuous price discovery",
          "Institutional EMS/OMS workflows for asset issuers and investors",
          "DvP settlement paths with trusted custody partners",
          "Embedded KYC/AML compliance for market access",
        ],
      },
      {
        heading: "Trusted asset operations",
        items: [
          "Verified asset listings with performance context",
          "Clear documentation and transparency for issuers",
          "Secondary market liquidity for tokenized positions",
          "Operational controls for portfolio managers and compliance teams",
        ],
      },
    ],
    callout: {
      title: "Explore tokenized assets",
      text: "Bring RWA markets to your institutional clients with a compliant execution layer.",
      buttonText: "Request Demo",
      buttonLink: "/contact",
    },
  },
  platform: {
    title: "Platform",
    eyebrow: "Integrated product suite",
    subtitle: "A single platform for professional digital asset workflows.",
    description:
      "The platform brings OTC trading, electronic execution, and tokenized asset markets together with enterprise-grade controls and operational clarity.",
    sections: [
      {
        heading: "Modular product architecture",
        text: "Deploy the workflows your desk needs today and add new capabilities as your business evolves.",
        items: [
          "Mercury Pro: Advanced execution tools and multi-venue order routing",
          "Mercury OTC: Electronic RFQ/RFS workflow with integrated compliance",
          "Mercury RWA: Secondary markets for tokenized real-world assets",
          "White-label capabilities for branded client experiences",
        ],
      },
      {
        heading: "Operational governance",
        text: "Built for teams that require auditability, custody controls, and clear approvals.",
        items: [
          "Embedded KYC/AML and compliance workflow support",
          "Trade and settlement transparency with DvP-ready rails",
          "Role-based access controls and review gates",
          "Real-time monitoring with audit-ready logs",
        ],
      },
      {
        heading: "Institutional confidence",
        text: "Designed to support desks, asset issuers, and market makers with a disciplined operating model.",
        items: [
          "High reliability and low-latency performance",
          "Proven integrations with custody and settlement partners",
          "Transparent product governance and service-level visibility",
          "Expandable stack that supports growth without re-architecture",
        ],
      },
    ],
    callout: {
      title: "See the platform in action",
      text: "Request a demo to discuss how this infrastructure can fit your trading and tokenization workflow.",
      buttonText: "Request Demo",
      buttonLink: "/contact",
    },
  },
  roadmap: {
    title: "Roadmap",
    eyebrow: "What’s next",
    subtitle: "A clear plan for growth and platform delivery.",
    description:
      "Track our progress across property sourcing, marketplace capabilities, compliance features, and investor tools.",
    sections: [
      {
        heading: "Current phase",
        text: "We are focused on marketplace launch, investor onboarding, and refining property-level performance disclosures.",
        items: [
          "Primary marketplace live",
          "Investor onboarding workflow",
          "Initial property allocations",
        ],
      },
      {
        heading: "Next steps",
        text: "The next stage is broader product capability, secondary trading, and deeper portfolio analytics for investors.",
        items: [
          "Secondary trading support",
          "Expanded global property sourcing",
          "Advanced portfolio management tools",
        ],
      },
      {
        heading: "Future vision",
        text: "A platform where fractional property investing is as easy to manage as any modern digital portfolio.",
        items: [
          "More liquidity pathways",
          "Better investment reporting",
          "Greater asset diversification",
        ],
      },
    ],
  },
  "help-center": {
    title: "Help Center",
    eyebrow: "Support & resources",
    subtitle: "Answers for investors and institutions.",
    description:
      "Find detailed guidance on platform mechanics, account management, investment evaluation, and technical troubleshooting.",
    sections: [
      {
        heading: "Getting started",
        items: [
          "Account registration and identity verification (KYC) process",
          "Setting up two-factor authentication and security features",
          "Connecting your wallet and managing private keys",
          "Understanding accredited investor requirements and eligibility",
          "Making your first deposit and funding your account",
        ],
      },
      {
        heading: "How it works",
        items: [
          "Understanding tokenized real estate and fractional ownership",
          "How smart contracts manage asset custody and distributions",
          "Dividend and rent distribution mechanics and timelines",
          "Secondary market trading and liquidity pools",
          "Reading asset prospectuses and performance reports",
        ],
      },
      {
        heading: "Technical & security",
        items: [
          "Custody and blockchain settlement explained",
          "Smart contract audits and security certifications",
          "Navigating cross-chain bridges and multi-chain assets",
          "API documentation for developers and integrators",
          "Reporting smart contract bugs via our security program",
        ],
      },
      {
        heading: "Support channels",
        text: "Our support team is available Monday–Friday, 9am–6pm ET.",
        items: [
          "support@billiontowers.com — General inquiries and technical issues",
          "compliance@billiontowers.com — KYC, accreditation, and regulatory questions",
          "In-app live chat for urgent platform issues",
          "+252906262489 — Voice support for institutional clients",
        ],
      },
    ],
  },
  status: {
    title: "Platform Status",
    eyebrow: "Service health",
    subtitle: "Uptime, performance, and incident reporting.",
    description:
      "Billion Towers operates with institutional-grade reliability. We monitor all systems 24/7 and publish transparency reports.",
    sections: [
      {
        heading: "Current system status",
        items: [
          "Marketplace & Trading: Operational ✓",
          "API & Data Services: Operational ✓",
          "Custody & Settlement: Operational ✓",
          "Investor Portal: Operational ✓",
          "Blockchain Nodes: All chains operational ✓",
        ],
      },
      {
        heading: "Uptime SLA",
        text: "We maintain a 99.95% uptime service level agreement across all critical systems, backed by institutional infrastructure partners.",
        items: [
          "30-day uptime: 99.98%",
          "90-day uptime: 99.96%",
          "12-month uptime: 99.95%",
        ],
      },
      {
        heading: "Incident history",
        text: "All incidents are published with root cause analysis and remediation details within 48 hours of resolution.",
        items: [
          "May 15 — Brief API latency during Ethereum network congestion (5 mins, Resolved)",
          "April 3 — Planned maintenance: Smart contract upgrade (30 mins, Scheduled)",
          "No major incidents in 12 months",
        ],
      },
      {
        heading: "Planned maintenance",
        text: "We announce maintenance windows 7 days in advance via email, in-app notifications, and status page.",
      },
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    eyebrow: "Data protection",
    subtitle: "How we protect your information and respect your privacy.",
    description:
      "Billion Towers collects minimal personal data, handles it securely, and maintains transparency about usage. This policy explains your rights and our obligations.",
    sections: [
      {
        heading: "Information we collect",
        items: [
          "Identity verification: Name, date of birth, address, government ID (for KYC/AML)",
          "Financial information: Bank details, investment history, portfolio holdings",
          "Technical data: IP address, browser type, platform usage analytics",
          "Communications: Support tickets, feedback, and preference data",
        ],
      },
      {
        heading: "How we use your data",
        items: [
          "Regulatory compliance: KYC/AML verification, tax reporting (Form 1099)",
          "Platform operations: Account security, fraud detection, transaction processing",
          "Custody: Sharing identity and ownership data with regulated custodians",
          "Investor communications: Updates about your holdings, distribution reports",
          "Service improvements: Aggregated analytics (never individual user data)",
        ],
      },
      {
        heading: "Data security",
        items: [
          "Encryption: All data in transit (TLS 1.3) and at rest (AES-256)",
          "Access controls: Role-based permissions with multi-factor authentication",
          "Audits: Third-party security audits conducted quarterly",
          "Incident response: Breach notification within 72 hours if required",
          "No third-party ad tracking or data sales",
        ],
      },
      {
        heading: "Your rights",
        text: "Under GDPR, CCPA, and other regulations, you have the right to access, correct, delete, and port your personal data.",
        items: [
          "Request a copy of your data: privacy@billiontowers.com",
          "Request data correction or deletion (subject to regulatory holds)",
          "Export your data in machine-readable format",
          "Opt out of non-essential communications",
        ],
      },
    ],
  },
  "terms-of-service": {
    title: "Terms of Service",
    eyebrow: "Legal terms",
    subtitle: "Agreement governing your use of Billion Towers.",
    description:
      "Please read these terms carefully. By accessing Billion Towers, you agree to be bound by these terms, our Privacy Policy, and Risk Disclosure.",
    sections: [
      {
        heading: "Eligibility and compliance",
        text: "You represent that you are of legal age, have the authority to enter into agreements, and will comply with all applicable laws.",
        items: [
          "Accredited investor status may be required for certain assets",
          "Prohibited in OFAC-sanctioned countries and jurisdictions",
          "Tax residency and beneficial ownership must be disclosed",
          "Professional advice (legal, tax) is your responsibility to obtain",
        ],
      },
      {
        heading: "Account security and responsibility",
        text: "You are responsible for maintaining confidentiality of your credentials and for all activities under your account.",
        items: [
          "Enable multi-factor authentication on your account",
          "You assume liability for unauthorized access unless we determine fraud",
          "Report suspicious activity immediately to security@billiontowers.com",
          "Billion Towers has no liability for your lost/forgotten private keys",
        ],
      },
      {
        heading: "Investment and platform limitations",
        items: [
          "Billion Towers facilitates investment access only; we do not guarantee returns",
          "Smart contracts are audited but may contain unforeseen risks",
          "Custody partners hold assets; failure of custodian is your sole recourse",
          "Market prices may move rapidly; execution is at market prices",
        ],
      },
      {
        heading: "Limitation of liability",
        text: "To the maximum extent permitted by law, Billion Towers, its officers, and partners are not liable for indirect, incidental, or consequential damages from platform use or unavailability.",
      },
      {
        heading: "Indemnification",
        text: "You agree to indemnify Billion Towers against claims arising from your violation of these terms, unauthorized account access, or misuse of the platform.",
      },
      {
        heading: "Changes and termination",
        text: "We may modify these terms on 30 days' notice. We may terminate access for violations, regulatory requirements, or business reasons.",
      },
    ],
  },
  "risk-disclosure": {
    title: "Risk Disclosure",
    eyebrow: "Investment risks",
    subtitle: "Understand the risks before investing.",
    description:
      "Real estate investing carries market, liquidity, operational, and regulatory risk. Please review these disclosures carefully before participating in fractional property opportunities.",
    sections: [
      {
        heading: "Market risk",
        text: "Property values can move up or down in response to macroeconomic cycles, interest rates, local demand, and capital market conditions. Market risk may lead to lower valuations and reduced returns, especially during economic downturns.",
        items: [
          "Economic and interest rate shifts",
          "Local supply and demand changes",
          "Sector-specific performance variability",
        ],
      },
      {
        heading: "Performance variability",
        text: "Each investment’s return depends on property-level execution, tenant mix, leasing performance, and operational efficiency. Historical outcomes are not a reliable predictor of future performance.",
        items: [
          "Asset selection and underwriting quality",
          "Operating income and occupancy trends",
          "Unexpected repair or cost increases",
        ],
      },
      {
        heading: "Liquidity risk",
        text: "Some holdings may be harder to sell quickly depending on marketplace demand, asset type, and market conditions. Limited liquidity can delay exit timing and increase the cost of selling.",
        items: [
          "Secondary market depth may vary",
          "Time required to find a buyer",
          "Price impact of forced sales",
        ],
      },
      {
        heading: "Operational risk",
        text: "Properties depend on effective management, maintenance, and tenant performance. Operational challenges such as maintenance issues, tenant defaults, or management disruptions can reduce net returns.",
        items: [
          "Maintenance and capital expenditures",
          "Property management execution",
          "Tenant turnover and vacancy risk",
        ],
      },
      {
        heading: "Regulatory and legal risk",
        text: "Changes in zoning, tax rules, compliance requirements, or legal frameworks can affect asset performance and investor returns. Regulatory risk is an inherent part of real estate investment.",
      },
    ],
    callout: {
      title: "Invest responsibly",
      text: "Review your objectives, investment horizon, and risk tolerance before participating in fractional property investments.",
      buttonText: "Learn more",
      buttonLink: "/contact",
    },
  },
  compliance: {
    title: "Compliance & Regulatory Framework",
    eyebrow: "Institutional-grade governance",
    subtitle:
      "Regulatory compliance across all jurisdictions and asset classes.",
    description:
      "Billion Towers operates under a comprehensive regulatory framework with multiple registrations, audits, and partnerships designed to protect investors and ensure platform integrity.",
    sections: [
      {
        heading: "Regulatory registrations",
        text: "We maintain active registrations with primary financial regulators in all operating jurisdictions.",
        items: [
          "SEC Alternative Trading System (ATS) — Registered as national securities exchange for tokenized securities",
          "FinCEN Money Services Business (MSB) — Licensed for virtual asset transmission and custody",
          "OFAC Sanctions Program — Full compliance screening on all transactions and users",
          "State Money Transmitter Licenses — Registered in all applicable US states and territories",
          "NY BitLicense — Approved for virtual currency activities in New York",
        ],
      },
      {
        heading: "AML/KYC compliance",
        text: "Comprehensive anti-money laundering and know-your-customer programs to prevent financial crime.",
        items: [
          "Enhanced customer due diligence (EDD) for all investors above threshold amounts",
          "Continuous transaction monitoring with AI-powered suspicious activity detection",
          "Quarterly CTR (Currency Transaction Report) and SAR (Suspicious Activity Report) filings with FinCEN",
          "Sanctions screening via OFAC SDN list, secondary watchlists, and third-party data providers",
          "Identity verification via government-issued ID, biometric verification, and database matching",
          "PEP (Politically Exposed Person) screening for high-risk users and beneficial owners",
        ],
      },
      {
        heading: "Custody and settlement",
        text: "Professional custodians handle all asset settlement and custody with independent auditability.",
        items: [
          "BNY Mellon Pershing — Institutional-grade custody for tokenized securities and cash",
          "Anchorage Digital — Regulated digital asset custody and settlement provider",
          "BitGo — Insurance-backed custody for blockchain-native assets and tokens",
          "Settlement via Ethereum network with redundant blockchain nodes operated independently",
          "Multi-signature custody controls requiring multiple signatures for asset movements",
        ],
      },
      {
        heading: "Smart contract audits",
        text: "All platform and asset-related smart contracts undergo rigorous third-party audits.",
        items: [
          "OpenZeppelin — Formal verification and security audit of core platform contracts",
          "CertiK — Continuous security monitoring and blockchain audit of production contracts",
          "Trail of Bits — Advanced adversarial testing and asset-specific contract audits",
          "Public audit reports available for all material contracts via GitHub repositories",
          "Automated monitoring via Forta network for anomalous smart contract behavior",
        ],
      },
      {
        heading: "Disclosures and transparency",
        text: "Full transparency into platform operations, financial performance, and risk factors.",
        items: [
          "Form ATS-N filings published quarterly with SEC and market participation details",
          "Annual audited financial statements by Big Four accounting firm (KPMG)",
          "Monthly treasury reports and smart contract audit status published onchain",
          "Risk factor updates whenever material changes affect investor protections",
          "Public disclosure of all partnerships, funding, and material corporate events",
        ],
      },
      {
        heading: "Governance and oversight",
        text: "Independent governance structures ensure compliance decisions are protected from commercial pressures.",
        items: [
          "Compliance Committee with external board members and regulatory experts",
          "Annual third-party compliance audit with regulatory scope review",
          "Regular meetings with SEC, FinCEN, and state regulators",
          "Whistleblower program protecting employees reporting compliance concerns",
          "Public incident response policy with mandatory disclosure of security events",
        ],
      },
    ],
  },
  "how-it-works": {
    title: "How Tokenized Real Estate Works",
    eyebrow: "Platform mechanics",
    subtitle:
      "Understanding the technology and process behind fractional ownership.",
    description:
      "Tokenization enables real estate assets to be divided into digital ownership units, traded 24/7 on decentralized networks, with transparent performance tracking and settlement.",
    sections: [
      {
        heading: "Asset tokenization",
        text: "Real estate properties are registered onchain as verified digital representations with embedded economic rights.",
        items: [
          "Property valuation — Independent appraisers assess market value using comparable sales and income approach",
          "Legal documentation — Smart contract encodes ownership rights, dividend streams, and governance rules in immutable code",
          "Blockchain registration — Asset data is registered on Ethereum mainnet with settlement finality",
          "Custody integration — Registered custodian (BNY Mellon, Anchorage) holds asset title and manages distributions",
          "Fractional ownership — Property is divided into millions of individual ownership tokens (e.g., BT-PROP-2026-001 represents one share)",
        ],
      },
      {
        heading: "Investor participation",
        text: "Institutional and accredited investors access property opportunities through simple onboarding and transparent pricing.",
        items: [
          "Account setup — KYC verification, accreditation confirmation (SEC Form D), and compliance screening",
          "Wallet connection — Link a custodial wallet (e.g., MetaMask with Institutional custody) or cold storage address",
          "Asset discovery — Browse tokenized properties with full prospectus, financial projections, and risk disclosures",
          "Direct purchase — Buy ownership tokens at transparent NAV prices directly from asset sponsor or secondary market",
          "Instant settlement — Blockchain settlement confirms ownership within minutes; custody is updated immediately",
        ],
      },
      {
        heading: "Income distribution",
        text: "Property income (rent, appreciation, dividend, interest) is automatically calculated and distributed to token holders.",
        items: [
          "Cash flow tracking — Property manager submits monthly rental receipts and operating expenses to custodian",
          "Smart contract automation — Dividend contract automatically calculates distributions based on verified cash flows",
          "Stablecoin settlement — Income distributed as USDC (USD Coin), a regulated stablecoin 1:1 backed by USD reserves",
          "Real-time updates — Token holder dashboards update in real-time with accruing income and revised NAV",
          "Tax reporting — Platform generates Form 1099 and K-1 statements for all income distributions",
        ],
      },
      {
        heading: "Secondary market trading",
        text: "Token holders can sell holdings on the Billion Towers marketplace or compatible DeFi protocols without asset lock-up periods.",
        items: [
          "Order book trading — Market participants place buy/sell orders; platform matches at fair prices",
          "Automated Market Makers (AMMs) — Liquidity pools enable continuous trading with dynamic pricing",
          "Price discovery — Token prices adjust in real-time based on actual cash flows, market conditions, and demand",
          "Custody preservation — Trading occurs on-custody; custodian updates ownership ledger within hours",
          "Tax efficiency — FIFO and specific lot identification tools help optimize capital gains reporting",
        ],
      },
      {
        heading: "Compliance verification",
        text: "Every transaction runs through automated compliance screens to ensure regulatory adherence.",
        items: [
          "Accreditation verification — System confirms investor status (net worth $1M+ or $200K+ income) against federal database",
          "Sanctions screening — OFAC and secondary watchlist checks run on every transaction",
          "Transfer restrictions — Reg D and Rule 144 holdperiods are encoded in smart contracts and enforced automatically",
          "Qualified investor requirements — Certain asset classes restricted to sophisticated investors per offering documents",
          "Geographic compliance — Platform enforces jurisdiction-specific restrictions (OFAC countries, state regulations)",
        ],
      },
      {
        heading: "Performance reporting",
        text: "Investors receive monthly performance reports with property updates, financial metrics, and risk assessments.",
        items: [
          "Portfolio dashboard — Real-time view of holdings, NAV, accrued income, and portfolio allocation",
          "Property metrics — Occupancy rates, tenant quality, operating expenses, and maintenance activities",
          "Financial statements — Monthly P&L, quarterly balance sheets, and annual audited financials",
          "Risk metrics — Portfolio concentration, market conditions, regulatory developments, and custodian health",
          "Tax documents — Quarterly estimated tax statements, annual 1099s, and cap gains planning tools",
        ],
      },
    ],
    callout: {
      title: "Ready to invest?",
      text: "Learn more about our asset offerings and how to get started with institutional-grade fractional real estate investing.",
      buttonText: "Explore Assets",
      buttonLink: "/marketplace",
    },
  },
};
